import React, { useState, useEffect } from "react";
import { Grid, CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import { Link, useParams } from "react-router-dom";
import ArgonButton from "components/ArgonButton";
import PropTypes from "prop-types";
import { getAllRaiseInvoices } from "services/financeService";
import api from "utils/api";
import { formatCycleLabel } from "utils/formatCycleLabel";

function FinanceInvoices({ parentId: propParentId, onStatusChange }) {
  const { id } = useParams();
  const parentId = propParentId || id;

  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState([
    { label: "Unallocated Sessions", value: "0" },
    { label: "Available Balance", value: "₹ 0" },
    { label: "Total Paid", value: "₹ 0" },
    { label: "OTM Amount", value: "₹ 0" },
    { label: "Usually pays by", value: "Not Paid Yet" },
    { label: "Number of Payments", value: "0" },
  ]);
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [financeRows, setFinanceRows] = useState([]);

  // Invoice Table Columns
  const invoiceColumns = [
    { name: "S.No", align: "center" },
    { name: "Student Name", align: "left" },
    { name: "Invoice no", align: "center" },
    { name: "Type", align: "center" },
    { name: "Status", align: "center" },
    { name: "Amount", align: "center" },
    { name: "Date", align: "center" },
  ];

  // Finance Table Columns
  const financeColumns = [
    { name: "S.NO", align: "center" },
    { name: "STUDENT'S FEE", align: "center" },
    { name: "DATE OF PAYMENT RECEIVED", align: "center" },
    { name: "TENURE CHARGED", align: "center" },
    { name: "TOTAL FEE CHARGED", align: "center" },
    { name: "TOTAL SESSIONS", align: "center" },
    { name: "TUTOR SALARY", align: "center" },
    { name: "GROSS MARGIN", align: "center" },
  ];

  useEffect(() => {
    if (parentId) {
      fetchFinanceData();
    }
  }, [parentId]);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);

      // Fetch invoices and CRM data in parallel
      const [invoicesRes, crmRes] = await Promise.all([
        getAllRaiseInvoices({
          parentId: parentId,
          limit: 100,
        }),
        api.get(`/crm?parentId=${parentId}`).catch(() => ({ data: null })),
      ]);

      // Backend returns { success: true, data: [...], pagination: {...} }
      // Check if response is successful
      if (!invoicesRes || !invoicesRes.success) {
        setInvoiceRows([]);
        setFinanceRows([]);
        if (onStatusChange) {
          onStatusChange("error");
        }
        setLoading(false);
        return;
      }

      const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];

      const safeNumber = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const normalizeValue = (value) => String(value ?? "").trim().toUpperCase();

      // Get CRM entries with OTM payments
      const crmEntries = crmRes?.data?.data?.crmEntries || [];

      // Debug logging
      console.log("CRM Response:", crmRes?.data);
      console.log("CRM Entries:", crmEntries);

      // Filter OTM payments - check for COMPLETED paymentType with parentOTMAmount
      const otmPayments = crmEntries.filter((entry) => {
        const hasOTMAmount = safeNumber(entry?.parentOTMAmount) > 0;
        const isCompleted = normalizeValue(entry?.paymentType) === "COMPLETED";
        console.log(
          "Entry:",
          entry._id,
          "PaymentType:",
          entry.paymentType,
          "ParentOTMAmount:",
          entry.parentOTMAmount,
          "Include:",
          hasOTMAmount && isCompleted
        );
        return hasOTMAmount && isCompleted;
      });

      console.log("OTM Payments found:", otmPayments.length, otmPayments);

      // Update parent component status based on data availability
      if (onStatusChange) {
        onStatusChange(invoices.length > 0 ? "success" : "error");
      }

      // Calculate balance data from invoices
      const paidInvoices = invoices.filter((inv) => normalizeValue(inv?.status) === "PAID");

      const getTotalClasses = (invoice) => {
        const direct = safeNumber(invoice?.totalClasses);
        if (direct > 0) {
          return direct;
        }

        return safeNumber(invoice?.classSessionId?.[0]?.totalNoOfClasses);
      };

      const getSessionProgressClasses = (invoice) => {
        const sessionCompleted = safeNumber(invoice?.classSessionId?.[0]?.completedClasses);
        if (sessionCompleted > 0) {
          return sessionCompleted;
        }

        const direct = safeNumber(invoice?.totalCountedClasses);
        if (direct > 0) {
          return direct;
        }

        const completedAttendances = Array.isArray(invoice?.completedAttendances)
          ? invoice.completedAttendances
          : [];
        const extraClassCount = completedAttendances.reduce(
          (sum, att) => sum + safeNumber(att?.extraClassCount),
          0
        );
        const computed = completedAttendances.length + extraClassCount;

        return Math.max(direct, computed);
      };

      const getCountedClasses = (invoice) => {
        const direct = safeNumber(invoice?.totalCountedClasses);

        const completedAttendances = Array.isArray(invoice?.completedAttendances)
          ? invoice.completedAttendances
          : [];
        const extraClassCount = completedAttendances.reduce(
          (sum, att) => sum + safeNumber(att?.extraClassCount),
          0
        );
        const computed = completedAttendances.length + extraClassCount;

        return Math.max(direct, computed);
      };

      // Calculate total paid from regular invoices
      const invoiceTotalPaid = paidInvoices.reduce((sum, inv) => {
        return sum + safeNumber(inv?.totalAmount);
      }, 0);

      // Calculate total OTM amount from parentOTMAmount
      const otmTotalAmount = otmPayments.reduce((sum, entry) => {
        return sum + safeNumber(entry?.parentOTMAmount);
      }, 0);

      console.log("Invoice Total Paid:", invoiceTotalPaid);
      console.log("OTM Total Amount:", otmTotalAmount);

      // Total paid = invoices + OTM payments
      const totalPaid = invoiceTotalPaid + otmTotalAmount;

      console.log("Final Total Paid:", totalPaid);

      const numberOfPayments = paidInvoices.length + otmPayments.length;

      // Unallocated sessions = sessions remaining to be delivered for paid invoices
      const unallocatedSessions = paidInvoices.reduce((sum, inv) => {
        const totalClasses = getTotalClasses(inv);
        const countedClasses = getSessionProgressClasses(inv);
        const remaining = totalClasses - countedClasses;
        return sum + (remaining > 0 ? remaining : 0);
      }, 0);

      // Available balance = paid amount remaining after attended/consumed amount
      const availableBalance = paidInvoices.reduce((sum, inv) => {
        const totalAmount = safeNumber(inv?.totalAmount);
        const countedClasses = getCountedClasses(inv);

        const attendanceTotalAmount = safeNumber(inv?.attendanceTotalAmount);
        const perClassRate = safeNumber(inv?.perClassRate);
        const computedConsumed = perClassRate > 0 ? Math.round(perClassRate * countedClasses) : 0;
        const consumedAmount =
          attendanceTotalAmount > 0
            ? attendanceTotalAmount
            : computedConsumed > 0
            ? computedConsumed
            : 0;

        const remainingAmount = totalAmount - consumedAmount;
        return sum + (remainingAmount > 0 ? remainingAmount : 0);
      }, 0);

      // Get most common payment method
      const paymentMethods = paidInvoices
        .map((inv) => inv.paymentMethod || "Online")
        .filter(Boolean);
      const mostCommonMethod =
        paymentMethods.length > 0
          ? paymentMethods
              .sort(
                (a, b) =>
                  paymentMethods.filter((v) => v === a).length -
                  paymentMethods.filter((v) => v === b).length
              )
              .pop()
          : "Not Paid Yet";

      setBalanceData([
        { label: "Unallocated Sessions", value: unallocatedSessions.toString() },
        { label: "Available Balance", value: `₹ ${availableBalance.toLocaleString("en-IN")}` },
        { label: "Total Paid", value: `₹ ${totalPaid.toLocaleString("en-IN")}` },
        { label: "OTM Amount", value: `₹ ${otmTotalAmount.toLocaleString("en-IN")}` },
        { label: "Usually pays by", value: mostCommonMethod },
        { label: "Number of Payments", value: numberOfPayments.toString() },
      ]);

      // Process invoice rows
      const formattedInvoices = invoices.map((invoice, index) => ({
        "S.No": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {index + 1}
          </ArgonTypography>
        ),
        Date: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {new Date(invoice.createdAt).toLocaleDateString("en-IN")}
          </ArgonTypography>
        ),
        "Student Name": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {invoice.students && invoice.students.length > 0
              ? invoice.students.map((s) => s.name).join(", ")
              : "N/A"}
          </ArgonTypography>
        ),
        "Invoice no": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {invoice._id?.slice(-6) || "N/A"}
          </ArgonTypography>
        ),
        Type: (
          <ArgonTypography variant="caption" fontWeight="medium" color="text">
            {formatCycleLabel(invoice.totalCycles || invoice.classSessionId?.[0]?.noOfMonths)}
          </ArgonTypography>
        ),
        Status: (
          <ArgonBox
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              backgroundColor:
                invoice.status === "PAID"
                  ? "#d4edda"
                  : invoice.status === "PENDING"
                  ? "#fff3cd"
                  : invoice.status === "FAILED"
                  ? "#f8d7da"
                  : "#e2e3e5",
              color:
                invoice.status === "PAID"
                  ? "#155724"
                  : invoice.status === "PENDING"
                  ? "#856404"
                  : invoice.status === "FAILED"
                  ? "#721c24"
                  : "#383d41",
            }}
          >
            <ArgonTypography variant="caption" fontWeight="bold" sx={{ color: "inherit" }}>
              {invoice.status || "UNKNOWN"}
            </ArgonTypography>
          </ArgonBox>
        ),
        Amount: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            ₹ {invoice.totalAmount?.toLocaleString("en-IN") || "0"}
          </ArgonTypography>
        ),
      }));

      setInvoiceRows(formattedInvoices);

      // Process finance rows
      const formattedFinance = invoices.map((invoice, index) => ({
        "S.NO": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {index + 1}
          </ArgonTypography>
        ),
        "STUDENT'S FEE": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            ₹ {invoice.totalAmount?.toLocaleString("en-IN") || "0"}
          </ArgonTypography>
        ),
        "DATE OF PAYMENT RECEIVED": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {new Date(invoice.createdAt).toLocaleDateString("en-IN")}
          </ArgonTypography>
        ),
        "TENURE CHARGED": (
          <ArgonTypography variant="caption" fontWeight="medium" color="text">
            {formatCycleLabel(invoice.totalCycles || invoice.classSessionId?.[0]?.noOfMonths)}
          </ArgonTypography>
        ),
        "TOTAL FEE CHARGED": (
          <ArgonTypography variant="caption" fontWeight="medium" color="text">
            ₹ {invoice.totalAmount?.toLocaleString("en-IN") || "0"}
          </ArgonTypography>
        ),
        "TOTAL SESSIONS": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {typeof invoice.totalClasses === "number" ? invoice.totalClasses : "N/A"}
          </ArgonTypography>
        ),
        "TUTOR SALARY": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            ₹ {invoice.totalTutorPay?.toLocaleString("en-IN") || "0"}
          </ArgonTypography>
        ),
        "GROSS MARGIN": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            ₹ {invoice.grossMargin?.toLocaleString("en-IN") || "0"}
          </ArgonTypography>
        ),
      }));

      setFinanceRows(formattedFinance);
    } catch (error) {
      console.error("Error fetching finance data:", error);
      if (onStatusChange) {
        onStatusChange("error");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </ArgonBox>
    );
  }

  return (
    <ArgonBox py={3}>
      <ArgonBox
        p={3}
        sx={{
          backgroundColor: "#ffffffff",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        {/* Header */}
        <Grid container alignItems="center" justifyContent="space-between" mb={3}>
          <Grid item>
            <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
              Balance Details
            </ArgonTypography>
          </Grid>
          <Grid item>
            <ArgonButton
              component={Link}
              to={`/parents/invoice-history/${parentId}`}
              variant="contained"
              sx={{
                backgroundColor: "#3498db",
                color: "white",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: "600",
                "&:hover": {
                  backgroundColor: "#2980b9",
                },
                color: "white !important",
              }}
            >
              View Full History
            </ArgonButton>
          </Grid>
        </Grid>

        {/* Balance Info (Compact Cards) */}
        {/* Balance Info (Flex Responsive - 6 in a row) */}
        <ArgonBox
          display="flex"
          flexWrap="wrap"
          gap={1.5}
          sx={{
            justifyContent: "space-between", // evenly spread
          }}
        >
          {balanceData.map((item, index) => (
            <ArgonBox
              key={index}
              p={1.8}
              sx={{
                flex: "1 1 calc(16.66% - 12px)", // 👈 6 cards per row (16.66% each - gap adjust)
                maxWidth: "calc(16.66% - 12px)",
                minWidth: "140px", // 👈 responsiveness ke liye
                backgroundColor: "#ffffff",
                borderRadius: "10px",
                border: "1px solid #e0e0e0",
                textAlign: "center",
                minHeight: 85,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <ArgonTypography
                variant="body2"
                fontWeight="medium"
                color="text"
                sx={{ mb: 0.5, fontSize: "12px" }}
              >
                {item.label}
              </ArgonTypography>
              <ArgonTypography
                variant="body2"
                fontWeight="bold"
                sx={{ fontSize: "14px", color: "#1976d2" }}
              >
                {item.value}
              </ArgonTypography>
            </ArgonBox>
          ))}
        </ArgonBox>
      </ArgonBox>

      {/* Invoices Table */}
      <ArgonBox py={3}>
        <ArgonBox mb={2}>
          <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
            Invoice History
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
        >
          {invoiceRows.length > 0 ? (
            <Table columns={invoiceColumns} rows={invoiceRows} />
          ) : (
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="body2" color="text">
                No invoice records found
              </ArgonTypography>
            </ArgonBox>
          )}
        </ArgonBox>
      </ArgonBox>

      {/* Finance Details Table */}
      {/* <ArgonBox py={3}>
        <ArgonBox mb={2}>
          <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
            Payment Details
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
            overflowX: "auto",
          }}
        >
          {financeRows.length > 0 ? (
            <Table columns={financeColumns} rows={financeRows} />
          ) : (
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="body2" color="text">
                No payment records found
              </ArgonTypography>
            </ArgonBox>
          )}
        </ArgonBox>
      </ArgonBox> */}
    </ArgonBox>
  );
}

FinanceInvoices.propTypes = {
  parentId: PropTypes.string,
  onStatusChange: PropTypes.func,
};

export default FinanceInvoices;
